import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../App";

const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-background font-body flex flex-col">
    <header className="px-6 md:px-12 py-6">
      <Link to="/" className="inline-flex items-center gap-2">
        <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
        <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
          PostWriter
        </span>
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-sm text-center">{children}</div>
    </main>
  </div>
);

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token.");
      return;
    }

    const verify = async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("success");
        setMessage(data.message);
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.detail?.message || "This verification link is invalid.");
      }
    };

    verify();
  }, [searchParams, navigate]);

  return (
    <AuthShell>
      <Helmet>
        <title>Verify email — PostWriter</title>
      </Helmet>
      {status === "loading" && (
        <div className="animate-pulse text-muted-foreground text-sm">Verifying your email…</div>
      )}
      {status === "success" && (
        <>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <CheckCircle2 className="w-5 h-5 text-primary" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            Email verified
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Taking you to the login page…
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <XCircle className="w-5 h-5 text-destructive" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            Verification failed
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">{message}</p>
          <Button className="w-full" onClick={() => navigate("/login")}>
            Go to login
          </Button>
        </>
      )}
    </AuthShell>
  );
};

export default VerifyEmailPage;
